import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/core/utils/cn';

export interface WheelPickerItemStyle {
  fontSize: number;
  opacity: number;
}

interface WheelPickerProps<T> {
  items: T[];
  index: number;
  onChange: (index: number) => void;
  renderItem: (item: T, style: WheelPickerItemStyle) => React.ReactNode;
  itemHeight?: number;
  visibleCount?: number;
  className?: string;
  /** Set false when a parent renders one shared highlight band across multiple side-by-side wheels. */
  showHighlight?: boolean;
  /** Horizontal alignment of each item within the wheel's width. Defaults to 'center'. */
  align?: 'center' | 'end';
}

function itemStyle(distance: number): WheelPickerItemStyle {
  if (distance < 0.6) return { fontSize: 23, opacity: 1 };
  if (distance < 1.5) return { fontSize: 19, opacity: 0.35 };
  if (distance < 2.5) return { fontSize: 15, opacity: 0.2 };
  return { fontSize: 11, opacity: 0.1 };
}

/**
 * Vertically scrollable, snap-to-center wheel picker for an arbitrary item list —
 * generalises `ScrollableNumbers`' scroll/snap/fade mechanics beyond plain numbers
 * (e.g. calendar days) via `renderItem`.
 */
export function WheelPicker<T>({
  items,
  index,
  onChange,
  renderItem,
  itemHeight = 31,
  visibleCount = 7,
  className,
  showHighlight = true,
  align = 'center',
}: WheelPickerProps<T>) {
  const pad = Math.floor(visibleCount / 2);
  const containerH = itemHeight * visibleCount;

  const [scrollTop, setScrollTop] = useState(() => index * itemHeight);
  const listRef = useRef<HTMLDivElement>(null);
  const snapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the last index notified via onChange to prevent the sync effect
  // from re-firing when the parent echoes the same index back as a prop update.
  // NaN ensures the sync effect always fires on mount to set the initial scroll position.
  const lastNotified = useRef(NaN);

  useEffect(() => {
    if (lastNotified.current === index) return;
    lastNotified.current = index;
    const target = index * itemHeight;
    setScrollTop(target);
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = target;
    });
  }, [index, itemHeight]);

  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    setScrollTop(listRef.current.scrollTop);
    if (snapTimer.current) clearTimeout(snapTimer.current);
    snapTimer.current = setTimeout(() => {
      if (!listRef.current) return;
      const idx = Math.round(listRef.current.scrollTop / itemHeight);
      const clamped = Math.max(0, Math.min(idx, items.length - 1));
      listRef.current.scrollTop = clamped * itemHeight;
      setScrollTop(clamped * itemHeight);
      lastNotified.current = clamped;
      onChange(clamped);
    }, 80);
  }, [items.length, itemHeight, onChange]);

  const centerIndex = scrollTop / itemHeight;

  return (
    <div className={className ?? 'relative w-full'} style={{ height: containerH }}>
      {showHighlight && (
        <div
          className="pointer-events-none absolute inset-x-6.25 z-0 rounded-lg bg-[#eaeaea]"
          style={{ top: itemHeight * pad, height: 34 }}
        />
      )}
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="relative z-10 h-full scrollbar-none overflow-y-scroll"
        style={{ scrollSnapType: 'y mandatory' }}
      >
        <div style={{ height: itemHeight * pad }} />
        {items.map((item, i) => {
          const distance = Math.abs(i - centerIndex);
          return (
            <div
              key={i}
              style={{ height: itemHeight, scrollSnapAlign: 'center' }}
              className={cn('flex items-center', align === 'end' ? 'justify-end' : 'justify-center')}
            >
              {renderItem(item, itemStyle(distance))}
            </div>
          );
        })}
        <div style={{ height: itemHeight * pad }} />
      </div>
    </div>
  );
}
